import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminProvider } from '@shared/types.ts';
import { api, type ProviderInput } from '@/lib/api.ts';
import { Badge, ConfirmButton, Field, Problem, Switch } from './bits.tsx';
import { ImportPanel } from './ImportPanel.tsx';

/**
 * THE ACCOUNTS CHANNELS COME FROM.
 *
 * The screen this whole browser editor exists for: an Xtream line is a
 * hostname, a username and a password, and typing those on a D-pad is the
 * single most miserable thing this app could ask anybody to do.
 *
 * THE PASSWORD ONLY EVER GOES ONE WAY. Nothing reads a stored credential back
 * — `hasSecret` is the only thing the server will say about one — so the
 * field says "change password" rather than showing dots, and leaving it empty
 * means "keep what is there" rather than "clear it". Same rule the Seerr
 * sign-in follows, for the same reason: every displayed copy of a credential
 * is a place it can leak from.
 */

const KINDS: Array<{ id: ProviderInput['kind']; name: string; blurb: string }> = [
  { id: 'xtream', name: 'Xtream Codes', blurb: 'A host, a username and a password.' },
  { id: 'm3u', name: 'M3U playlist', blurb: 'A playlist URL, and optionally an XMLTV guide.' },
  { id: 'iheart', name: 'iHeart Radio', blurb: 'A public API — nothing to configure.' },
  { id: 'sxm', name: 'SiriusXM', blurb: 'Credentials come from the server’s .env.' },
  { id: 'custom', name: 'Custom URLs', blurb: 'For channels whose address you enter by hand.' },
];

function kindName(kind: string): string {
  return KINDS.find((k) => k.id === kind)?.name ?? kind;
}

/** Which kinds can actually be pulled from. The rest have no `list()`. */
function importable(kind: string): boolean {
  return kind === 'xtream' || kind === 'm3u' || kind === 'iheart';
}

export function Providers() {
  const { data: providers, isLoading, error } = useQuery({
    queryKey: ['admin', 'providers'],
    queryFn: () => api.adminProviders(),
    staleTime: 10_000,
  });

  const [adding, setAdding] = useState(false);

  if (isLoading) return <div className="muted center">Loading providers…</div>;
  if (error) return <Problem error={error} />;

  return (
    <>
      <p className="muted admin-note">
        Every channel in the lineup belongs to one of these. Switching an account off
        leaves its channels in place but stops them resolving — which is what you want
        the week a subscription lapses, and is not the same as deleting it.
      </p>

      <div className="provider-list">
        {(providers ?? []).map((p) => <ProviderCard key={p.id} provider={p} />)}
        {providers?.length === 0 && (
          <p className="muted">No providers yet.</p>
        )}
      </div>

      {adding
        ? <AddProvider onDone={() => setAdding(false)} />
        : (
          <button className="btn primary admin-add" onClick={() => setAdding(true)}>
            Add a provider
          </button>
        )}
    </>
  );
}

function ProviderCard({ provider: p }: { provider: AdminProvider }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<null | 'edit' | 'import'>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
  };

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.adminProviderPatch(p.id, { enabled }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: () => api.adminProviderDelete(p.id),
    onSuccess: refresh,
  });

  return (
    <section className={`provider${p.enabled ? '' : ' is-off'}`}>
      <header className="provider-head">
        <div className="provider-who">
          <div className="admin-name">{p.name}</div>
          <div className="muted admin-sub">
            <Badge>{kindName(p.kind)}</Badge>
            {' '}
            {p.configured
              ? <Badge tone="good">configured</Badge>
              : <Badge tone="warn">needs setup</Badge>}
            {' '}
            {/* The number that decides whether deleting it is even offered. */}
            <Badge tone={p.channels ? undefined : 'off'}>
              {p.channels} channel{p.channels === 1 ? '' : 's'}
            </Badge>
            {p.fromEnv && <> {' '}<Badge>from .env</Badge></>}
          </div>
          <div className="muted admin-sub provider-detail">
            {p.kind === 'xtream' && (p.host
              ? <>{p.host}{p.username ? ` · ${p.username}` : ''} · {p.hasSecret ? 'password stored' : 'no password'}</>
              : 'no host set')}
            {p.kind === 'm3u' && (p.playlistUrl
              ? <>{p.playlistUrl}{p.epgUrl ? ' · guide set' : ' · no guide URL'}</>
              : 'no playlist URL')}
          </div>
        </div>

        <div className="provider-actions">
          <Switch
            on={p.enabled}
            disabled={toggle.isPending}
            label={`${p.name} available`}
            onChange={(v) => toggle.mutate(v)}
          />
          <button
            className="btn"
            onClick={() => setOpen(open === 'edit' ? null : 'edit')}
          >
            {open === 'edit' ? 'Close' : 'Edit'}
          </button>
          {importable(p.kind) && (
            <button
              className="btn"
              disabled={!p.configured}
              title={p.configured ? undefined : 'Finish setting this account up first'}
              onClick={() => setOpen(open === 'import' ? null : 'import')}
            >
              {open === 'import' ? 'Close' : 'Import channels'}
            </button>
          )}
          {/*
            REFUSED BY THE SERVER while channels still point at it, with the
            count. No confirm dialog here for that reason: the refusal IS the
            confirmation, and it arrives with the number that matters.
          */}
          <ConfirmButton
            disabled={remove.isPending}
            onConfirm={() => remove.mutate()}
            armedLabel={p.channels ? `Delete — ${p.channels} channels point here` : 'Really delete?'}
          >
            Delete
          </ConfirmButton>
        </div>
      </header>

      <Problem error={toggle.error ?? remove.error} />

      {open === 'edit' && (
        <ProviderForm
          provider={p}
          onDone={() => { setOpen(null); refresh(); }}
        />
      )}
      {open === 'import' && <ImportPanel provider={p} />}
    </section>
  );
}

function AddProvider({ onDone }: { onDone: () => void }) {
  const [kind, setKind] = useState<ProviderInput['kind']>('xtream');
  const chosen = KINDS.find((k) => k.id === kind)!;

  return (
    <section className="admin-section provider-new">
      <h3 className="admin-subhead">Add a provider</h3>

      <div className="kind-choice">
        {KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            className={`kind-option${k.id === kind ? ' is-on' : ''}`}
            onClick={() => setKind(k.id)}
          >
            <span className="kind-option-name">{k.name}</span>
            <span className="kind-option-blurb">{k.blurb}</span>
          </button>
        ))}
      </div>

      <ProviderForm
        key={kind}
        newKind={kind}
        onDone={onDone}
        onCancel={onDone}
      />
      <p className="muted admin-field-hint">{chosen.blurb}</p>
    </section>
  );
}

/**
 * One form for both adding and editing.
 *
 * Deliberately not two. The fields, the validation and the "blank password
 * means unchanged" rule are identical, and the day they are two components is
 * the day the edit form grows a field the add form does not have.
 */
function ProviderForm({
  provider, newKind, onDone, onCancel,
}: {
  provider?: AdminProvider;
  newKind?: ProviderInput['kind'];
  onDone: () => void;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const kind = provider?.kind ?? newKind ?? 'xtream';

  const [name, setName] = useState(provider?.name ?? '');
  const [host, setHost] = useState(provider?.host ?? '');
  const [username, setUsername] = useState(provider?.username ?? '');
  const [password, setPassword] = useState('');
  const [playlistUrl, setPlaylistUrl] = useState(provider?.playlistUrl ?? '');
  const [epgUrl, setEpgUrl] = useState(provider?.epgUrl ?? '');

  const save = useMutation({
    mutationFn: () => {
      const body: ProviderInput = { name: name.trim() };
      if (kind === 'xtream') {
        body.host = host.trim();
        body.username = username.trim();
        if (password) body.password = password;
      }
      if (kind === 'm3u') {
        body.playlistUrl = playlistUrl.trim();
        body.epgUrl = epgUrl.trim();
      }
      if (provider) return api.adminProviderPatch(provider.id, body);
      return api.adminProviderCreate({ ...body, kind: kind as ProviderInput['kind'] });
    },
    onSuccess: () => {
      setPassword('');
      void qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
      onDone();
    },
  });

  /* A new Xtream line has nothing to fall back on, so it needs its password
     at birth — the server says so too. An existing one already has one. */
  const needsPassword = kind === 'xtream' && !provider && !password;
  const needsPlaylist = kind === 'm3u' && !playlistUrl.trim();
  const blocked = !name.trim() || needsPassword || needsPlaylist || save.isPending;

  return (
    <form
      className="provider-form"
      onSubmit={(e) => { e.preventDefault(); if (!blocked) save.mutate(); }}
    >
      <Field label="Name" hint="What you call it. Shown on the lineup screen.">
        <input
          className="seerr-input"
          value={name}
          placeholder={kindName(kind)}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      {kind === 'xtream' && (
        <>
          <Field label="Host" hint="http://line.example.com:8080 — no trailing path.">
            <input
              className="seerr-input"
              value={host}
              placeholder="http://line.example.com:8080"
              onChange={(e) => setHost(e.target.value)}
            />
          </Field>
          <Field label="Username">
            <input
              className="seerr-input"
              value={username}
              autoComplete="off"
              onChange={(e) => setUsername(e.target.value)}
            />
          </Field>
          <Field
            label="Password"
            hint={provider?.hasSecret
              ? 'A password is stored. Leave this blank to keep it.'
              : 'Sealed on the server and never read back out.'}
          >
            <input
              className="seerr-input"
              type="password"
              value={password}
              autoComplete="new-password"
              placeholder={provider?.hasSecret ? 'unchanged' : ''}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        </>
      )}

      {kind === 'm3u' && (
        <>
          <Field label="Playlist URL" hint="The .m3u or .m3u8 address.">
            <input
              className="seerr-input"
              value={playlistUrl}
              placeholder="https://example.com/playlist.m3u8"
              onChange={(e) => setPlaylistUrl(e.target.value)}
            />
          </Field>
          <Field
            label="Guide URL"
            hint="XMLTV, optional. Without it these channels have no programme data."
          >
            <input
              className="seerr-input"
              value={epgUrl}
              placeholder="https://example.com/guide.xml"
              onChange={(e) => setEpgUrl(e.target.value)}
            />
          </Field>
        </>
      )}

      <div className="provider-form-actions">
        <button className="btn primary" type="submit" disabled={blocked}>
          {save.isPending ? 'Saving…' : provider ? 'Save' : 'Add'}
        </button>
        {onCancel && (
          <button className="btn" type="button" onClick={onCancel}>Cancel</button>
        )}
      </div>

      <div className="provider-form-problem"><Problem error={save.error} /></div>
    </form>
  );
}
